import { Anchor } from "../../app/components/anchor"
import { useRouter } from "next/router"
import Image from "next/image"
import PrimaryButton from "../../components/buttons/PrimaryButton"
import CheckList from "../../components/content/CheckList"
import stateCityDictionary, {
  formatName,
  urlifyName,
} from "../../constants/stateCityDictionary"
import { BreedHalfStarRating } from "../../components/BreedStarRating"
import type { TransporterRow } from "../../services/cache"
import Avatar from "../../assets/icons/avatar.svg"

interface ITopTransportersProps {
  topTransporters: Array<TransporterRow & { hasActiveProfile?: boolean }>
}

export default function TopTransporters({
  topTransporters,
}: ITopTransportersProps) {
  const { query } = useRouter()
  const { city, state } = query

  const formattedState = formatName(state as string)
  const urlifiedCity = city ? urlifyName(city as string) : ""

  const originalCity =
    stateCityDictionary[formattedState]?.cities.find(
      (dictCity) => urlifyName(dictCity) === urlifiedCity,
    ) ?? ""

  const formattedCity = originalCity ? formatName(originalCity) : ""
  const stateAbbrev = stateCityDictionary[formattedState]?.abbreviation ?? ""

  const locationName = formattedCity
    ? `${formattedCity}, ${stateAbbrev}`
    : formattedState

  // Check if transporters data exists
  const hasTransportersData = topTransporters && topTransporters.length > 0

  return (
    <section className="my-16">
      <h3 className="max-w-[40ch] mx-auto mb-16 font-merriweather text-3xl text-center leading-[48px]">
        We are committed to building a safe, caring environment for people and
        animals on the road
      </h3>
      <section
        className={`grid grid-cols-1 mx-auto ${
          hasTransportersData ? "md:grid-cols-2 lg:max-w-5xl" : "lg:max-w-2xl"
        } gap-10`}
      >
        {hasTransportersData && (
          <aside className="bg-white drop-shadow-md rounded-lg max-w-lg">
            <h4 className="p-4 bg-secondary-100 flex items-center">
              Best Pet Shipping Companies in {locationName}
            </h4>
            {/*
              Anchor for the visual-regression gate, which strips this block
              before capture: the driver count comes from live marketplace data,
              so its height varies. Wraps only the mapped rows, so the card, its
              location-derived heading, and the adjacent CheckList and CTA all
              stay in the pixel diff. See cypress/e2e/pet-shipping-visual.cy.ts.
            */}
            <div data-testid="top-transporters-list">
              {topTransporters?.map(
                ({ photo, dba, avgRating, userId, hasActiveProfile }) => {
                  const rating = Number(Number(avgRating).toFixed(1))
                  const linked = Boolean(userId) && hasActiveProfile
                  const className = "p-6 border-b last:border-none flex"
                  const content = (
                    <>
                      <Image
                        src={photo || Avatar.src}
                        alt={`${dba} profile photo`}
                        width={56}
                        height={56}
                        loading="lazy"
                        className="h-14 w-14 mr-4 rounded-full overflow-hidden object-cover"
                      />

                      <div>
                        <p className="mb-0 text-lg">{dba}</p>
                        <div className="flex items-center text-sm">
                          <BreedHalfStarRating value={rating} />
                          {rating}/5
                        </div>
                      </div>
                    </>
                  )
                  return linked ? (
                    <Anchor
                      key={dba}
                      href={`/drivers/${userId}`}
                      className={`${className} no-underline`}
                    >
                      {content}
                    </Anchor>
                  ) : (
                    <div key={dba} className={className}>
                      {content}
                    </div>
                  )
                },
              )}
            </div>
          </aside>
        )}
        <div className="flex flex-col items-center justify-center">
          <CheckList
            className="w-full"
            items={[
              "We carefully background check all drivers.",
              "We verify transporter addresses and contact information for additional protection.",
              "Photos of drivers and their vehicles help confirm who and what to expect.",
              "Detailed customer reviews provide insight and transparency to your selection process.",
            ]}
          />

          <Anchor href="/shipments/new/animals" passHref>
            <PrimaryButton>Find A Loving Pet Transporter</PrimaryButton>
          </Anchor>
        </div>
      </section>
    </section>
  )
}
